import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

interface Schedule {
  id: string;
  departure: string;
  arrival: string;
  duration: string;
  price: number;
  amenities: string[];
  bus: string;
  busColor: string;
}

const SCHEDULES: Schedule[] = [
  { id: '1', departure: '4:00 AM',  arrival: '7:30 AM',  duration: '3h 30m', price: 9, amenities: ['Wi-Fi', 'AC', 'USB Charging'], bus: 'Phoenix I',  busColor: '#C9A84C' },
  { id: '2', departure: '8:30 AM',  arrival: '12:00 PM', duration: '3h 30m', price: 9, amenities: ['Wi-Fi', 'AC', 'USB Charging'], bus: 'Phoenix II', busColor: '#16a34a' },
  { id: '3', departure: '1:00 PM',  arrival: '4:30 PM',  duration: '3h 30m', price: 9, amenities: ['Wi-Fi', 'AC', 'USB Charging'], bus: 'Phoenix I',  busColor: '#C9A84C' },
  { id: '4', departure: '5:30 PM',  arrival: '9:00 PM',  duration: '3h 30m', price: 9, amenities: ['Wi-Fi', 'AC', 'USB Charging'], bus: 'Phoenix II', busColor: '#16a34a' },
];

const AMENITY_ICONS: Record<string, string> = {
  'Wi-Fi': 'ri-wifi-line',
  'AC': 'ri-temp-cold-line',
  'USB Charging': 'ri-plug-line',
  'Snacks': 'ri-cup-line',
};

const TERMINALS = [
  {
    city: 'Panama City',
    name: 'Gran Terminal Nacional de Transporte',
    address: 'Ave. Frangipani, El Dorado, Panama City, Panama',
    phone: '+507 232-5100',
    hours: 'Daily 04:00 AM – 10:00 PM',
    mapSrc: 'https://maps.google.com/maps?q=Gran+Terminal+Nacional+de+Transporte,+El+Dorado,+Panama+City,+Panama&output=embed&hl=en&z=15',
  },
  {
    city: 'Chitré',
    name: 'Terminal de Transporte de Chitré',
    address: 'Ave. Obaldía, Chitré, Herrera, Panama',
    phone: '+507 996-4022',
    hours: 'Daily 04:00 AM – 10:00 PM',
    mapSrc: 'https://maps.google.com/maps?q=Terminal+de+Transporte+de+Chitre,+Herrera,+Panama&output=embed&hl=en&z=15',
  },
];

function navigateToContact() {
  setTimeout(() => {
    window.REACT_APP_NAVIGATE?.('/');
    setTimeout(() => {
      const contactEl = document.querySelector('#contact');
      if (contactEl) contactEl.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, 300);
}

interface ScheduleCardProps {
  schedule: Schedule;
  fromCity: string;
  toCity: string;
  tripType: 'one-way' | 'round-trip';
  isSelected: boolean;
  onSelect: () => void;
}

function ScheduleCard({ schedule, fromCity, toCity, tripType, isSelected, onSelect }: ScheduleCardProps) {
  const { t } = useTranslation();
  return (
    <div
      className={`bg-white border-2 rounded-lg p-5 transition-all duration-300 ${
        isSelected ? 'border-[#C9A84C] bg-[#C9A84C]/5' : 'border-gray-100 hover:border-[#C9A84C]/40'
      }`}
    >
      {/* Bus badge + selected indicator */}
      <div className="flex items-center justify-between mb-3">
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-white text-[11px] font-bold uppercase tracking-wider"
          style={{ backgroundColor: schedule.busColor }}
        >
          <i className="ri-bus-2-line text-xs"></i>
          {schedule.bus}
        </span>
        {isSelected && (
          <div className="flex items-center gap-1 text-[#C9A84C] text-xs font-semibold uppercase tracking-wider">
            <i className="ri-checkbox-circle-fill text-base"></i>
            {t('sched_selected_badge')}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="text-center">
          <div className="text-xl font-bold text-gray-900 font-playfair">{schedule.departure}</div>
          <div className="text-xs text-gray-500 mt-0.5 uppercase tracking-wide">{fromCity}</div>
        </div>
        <div className="flex flex-col items-center gap-1 flex-1 px-3">
          <div className="flex items-center w-full gap-1.5">
            <div className="h-px bg-gray-200 flex-1"></div>
            <i className="ri-bus-line text-[#C9A84C] text-base"></i>
            <div className="h-px bg-gray-200 flex-1"></div>
          </div>
          <span className="text-xs text-gray-400 whitespace-nowrap">{schedule.duration}</span>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-gray-900 font-playfair">{schedule.arrival}</div>
          <div className="text-xs text-gray-500 mt-0.5 uppercase tracking-wide">{toCity}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {schedule.amenities.map((amenity) => (
          <span key={amenity} className="flex items-center gap-1 px-2.5 py-1 bg-gray-50 text-gray-600 text-xs font-medium rounded-full">
            <i className={`${AMENITY_ICONS[amenity] ?? 'ri-star-line'} text-[#C9A84C]`}></i>
            {amenity}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div>
          <div className="text-xs text-gray-400 uppercase tracking-wider">{t('sched_price_label')}</div>
          <div className="text-2xl font-bold text-[#C9A84C] font-playfair">${schedule.price}</div>
        </div>
        {tripType === 'one-way' ? (
          <button
            onClick={navigateToContact}
            className="px-5 py-2.5 bg-[#C9A84C] hover:bg-[#b8953e] text-white rounded-full text-sm font-semibold uppercase tracking-wider transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('sched_bookNow')}
          </button>
        ) : (
          <button
            onClick={onSelect}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold uppercase tracking-wider transition-all duration-300 whitespace-nowrap cursor-pointer ${
              isSelected
                ? 'bg-[#C9A84C] text-white'
                : 'border-2 border-[#C9A84C] text-[#C9A84C] hover:bg-[#C9A84C] hover:text-white'
            }`}
          >
            {isSelected ? t('sched_selected_btn') : t('sched_select')}
          </button>
        )}
      </div>
    </div>
  );
}

export default function SchedulesPage() {
  const { t } = useTranslation();
  const [tripType, setTripType] = useState<'one-way' | 'round-trip'>('one-way');
  const [outboundId, setOutboundId] = useState<string | null>(null);
  const [returnId, setReturnId] = useState<string | null>(null);

  const outbound = SCHEDULES.find((s) => s.id === outboundId);
  const returnTrip = SCHEDULES.find((s) => s.id === returnId);
  const totalPrice = (outbound?.price ?? 0) + (returnTrip?.price ?? 0);
  const showSummary = tripType === 'round-trip' && (outboundId || returnId);

  const handleTripTypeChange = (type: 'one-way' | 'round-trip') => {
    setTripType(type);
    setOutboundId(null);
    setReturnId(null);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Header */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <button
            onClick={() => window.REACT_APP_NAVIGATE?.('/')}
            className="inline-flex items-center gap-2 text-white/60 hover:text-[#C9A84C] text-sm font-medium tracking-wider uppercase transition-colors duration-300 cursor-pointer mb-8 group"
          >
            <span className="w-7 h-7 flex items-center justify-center rounded-full border border-white/20 group-hover:border-[#C9A84C] transition-colors duration-300">
              <i className="ri-arrow-left-line text-sm"></i>
            </span>
            {t('sched_backHome')}
          </button>
          <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            {t('sched_title')}
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto">
            {t('sched_subtitle')}
          </p>
        </div>
      </section>

      {/* Route Stats */}
      <section className="py-10 bg-gray-50 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-center justify-center gap-10">
            {[
              { icon: 'ri-map-pin-line', labelKey: 'sched_stat_distance_label', value: '248 km' },
              { icon: 'ri-time-line', labelKey: 'sched_stat_duration_label', value: '3h 30m' },
              { icon: 'ri-calendar-line', labelKey: 'sched_stat_departures_label', valueKey: 'sched_stat_departures_val' },
              { icon: 'ri-price-tag-3-line', labelKey: 'sched_stat_price_label', valueKey: 'sched_stat_price_val' },
            ].map((stat) => (
              <div key={stat.labelKey} className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#C9A84C]/10 rounded-full flex items-center justify-center">
                  <i className={`${stat.icon} text-xl text-[#C9A84C]`}></i>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">{t(stat.labelKey)}</div>
                  <div className="text-base font-semibold text-gray-900">
                    {stat.valueKey ? t(stat.valueKey) : stat.value}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trip Type Toggle */}
      <section className="pt-14 pb-2 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex justify-center mb-2">
            <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
              <button
                onClick={() => handleTripTypeChange('one-way')}
                className={`px-7 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  tripType === 'one-way' ? 'bg-[#C9A84C] text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-arrow-right-line mr-2"></i>{t('sched_oneWay')}
              </button>
              <button
                onClick={() => handleTripTypeChange('round-trip')}
                className={`px-7 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  tripType === 'round-trip' ? 'bg-[#C9A84C] text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-arrow-left-right-line mr-2"></i>{t('sched_roundTrip')}
              </button>
            </div>
          </div>
          {tripType === 'round-trip' && (
            <p className="text-center text-sm text-gray-500 mt-3">{t('sched_roundTrip_note')}</p>
          )}
        </div>
      </section>

      {/* Two-Column Schedule */}
      <section className={`py-12 bg-white ${showSummary ? 'pb-40' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Panama City → Chitré */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-[#C9A84C] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-right-line text-white text-lg"></i>
                </div>
                <div>
                  <h2 className="font-playfair text-xl font-bold text-gray-900">{t('sched_col1_title')}</h2>
                  <p className="text-sm text-gray-500">
                    {tripType === 'round-trip' ? t('sched_col1_sub_rt') : t('sched_col1_sub_ow')}
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-4">
                {SCHEDULES.map((schedule) => (
                  <ScheduleCard
                    key={`pc-${schedule.id}`}
                    schedule={schedule}
                    fromCity="Panama City"
                    toCity="Chitré"
                    tripType={tripType}
                    isSelected={outboundId === schedule.id}
                    onSelect={() => setOutboundId(outboundId === schedule.id ? null : schedule.id)}
                  />
                ))}
              </div>
            </div>

            {/* Chitré → Panama City */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-[#1a1a1a] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-left-line text-white text-lg"></i>
                </div>
                <div>
                  <h2 className="font-playfair text-xl font-bold text-gray-900">{t('sched_col2_title')}</h2>
                  <p className="text-sm text-gray-500">
                    {tripType === 'round-trip' ? t('sched_col2_sub_rt') : t('sched_col2_sub_ow')}
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-4">
                {SCHEDULES.map((schedule) => (
                  <ScheduleCard
                    key={`ch-${schedule.id}`}
                    schedule={schedule}
                    fromCity="Chitré"
                    toCity="Panama City"
                    tripType={tripType}
                    isSelected={returnId === schedule.id}
                    onSelect={() => setReturnId(returnId === schedule.id ? null : schedule.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Round-Trip Summary Bar */}
      {showSummary && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-[#C9A84C]/30 px-6 py-4">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <i className="ri-arrow-right-line text-[#C9A84C] text-lg"></i>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wide">{t('sched_summary_outbound')}</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {outbound ? `${outbound.departure} · ${t('sched_outbound_detail')}` : t('sched_not_selected')}
                  </div>
                </div>
              </div>
              <div className="w-px h-8 bg-gray-200 hidden sm:block"></div>
              <div className="flex items-center gap-2">
                <i className="ri-arrow-left-line text-gray-700 text-lg"></i>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wide">{t('sched_summary_return')}</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {returnTrip ? `${returnTrip.departure} · ${t('sched_return_detail')}` : t('sched_not_selected')}
                  </div>
                </div>
              </div>
              <div className="w-px h-8 bg-gray-200 hidden sm:block"></div>
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wide">{t('sched_summary_total')}</div>
                <div className="text-xl font-bold text-[#C9A84C] font-playfair">${totalPrice}</div>
              </div>
            </div>
            <button
              onClick={navigateToContact}
              disabled={!outboundId || !returnId}
              className={`px-8 py-3 rounded-full text-sm font-semibold uppercase tracking-wider transition-all duration-300 whitespace-nowrap cursor-pointer ${
                outboundId && returnId
                  ? 'bg-[#C9A84C] hover:bg-[#b8953e] text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {outboundId && returnId ? t('sched_bookRoundTrip') : t('sched_selectBothLegs')}
            </button>
          </div>
        </div>
      )}

      {/* Terminals Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-[#C9A84C] text-xs font-semibold tracking-[0.3em] uppercase mb-3">{t('sched_terminals_eyebrow')}</p>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('sched_terminals_title')}
            </h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto">
              {t('sched_terminals_subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {TERMINALS.map((terminal) => (
              <div key={terminal.city} className="bg-white rounded-2xl overflow-hidden border border-gray-100">
                <div className="w-full h-64">
                  <iframe
                    title={`${terminal.city} Terminal Map`}
                    src={terminal.mapSrc}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 bg-[#C9A84C] rounded-full"></div>
                    <span className="text-xs text-[#C9A84C] font-semibold uppercase tracking-widest">{terminal.city}</span>
                  </div>
                  <h3 className="font-playfair text-xl font-bold text-gray-900 mb-4">{terminal.name}</h3>
                  <div className="space-y-3">
                    {[
                      { icon: 'ri-map-pin-line', labelKey: 'sched_terminal_address', value: terminal.address },
                      { icon: 'ri-phone-line', labelKey: 'sched_terminal_phone', value: terminal.phone },
                      { icon: 'ri-time-line', labelKey: 'sched_terminal_hours', value: terminal.hours },
                    ].map((item) => (
                      <div key={item.labelKey} className="flex items-start gap-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#C9A84C]/10 rounded-full flex-shrink-0 mt-0.5">
                          <i className={`${item.icon} text-[#C9A84C] text-sm`}></i>
                        </div>
                        <div>
                          <div className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">{t(item.labelKey)}</div>
                          <div className="text-sm text-gray-700">{item.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <h2 className="font-playfair text-3xl font-bold text-gray-900 mb-8 text-center">
            {t('sched_info_title')}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: 'ri-time-line', titleKey: 'sched_info_checkin_title', descKey: 'sched_info_checkin_desc' },
              { icon: 'ri-luggage-cart-line', titleKey: 'sched_info_baggage_title', descKey: 'sched_info_baggage_desc' },
              { icon: 'ri-refresh-line', titleKey: 'sched_info_cancel_title', descKey: 'sched_info_cancel_desc' },
              { icon: 'ri-ticket-line', titleKey: 'sched_info_ticket_title', descKey: 'sched_info_ticket_desc' },
            ].map((item) => (
              <div key={item.titleKey} className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C9A84C]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-xl text-[#C9A84C]`}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">{t(item.titleKey)}</h3>
                    <p className="text-gray-600 text-sm">{t(item.descKey)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
